import axios from 'axios';
import Cookies from 'js-cookie';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7271/api';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
}

export interface User {
  id: number;
  fullName: string;
  email: string;
  phone: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LoginResponse {
  token: string;
  user: User;
  expires: string;
}

export interface AuthResult {
  isSuccess: boolean;
  message: string;
  data?: any;
}

// Configure axios defaults
axios.defaults.baseURL = API_BASE_URL;

// Request interceptor to add JWT token to requests
axios.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      
      // Don't redirect to login for public pages - let them handle their own errors
      if (currentPath.startsWith('/sign/') || currentPath.startsWith('/folder-upload/')) {
        return Promise.reject(error);
      }
      
      // Store the current path to redirect back after login
      removeAuthToken();
      removeCurrentUser();
      
      // Redirect to login with return URL
      if (currentPath !== '/' && currentPath !== '/login') {
        window.location.href = `/login?returnUrl=${encodeURIComponent(currentPath)}`;
      } else {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Token management
export const getAuthToken = (): string | null => {
  return Cookies.get('auth_token') || null;
};

export const setAuthToken = (token: string, expiresIn?: string): void => {
  // Default to 1 day if no expiration provided
  let expires = 1;
  
  // Parse expiration from JWT response if provided (e.g., "2024-01-01T00:00:00Z")
  if (expiresIn) {
    const expirationDate = new Date(expiresIn);
    const now = new Date();
    const daysUntilExpiration = (expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    expires = Math.max(0.01, daysUntilExpiration); // Minimum 15 minutes
  }
  
  Cookies.set('auth_token', token, { 
    expires,
    secure: process.env.NODE_ENV === 'production',
    sameSite: 'lax'
  });
};

export const removeAuthToken = (): void => {
  Cookies.remove('auth_token');
};

// User management
export const getCurrentUser = (): User | null => {
  const userStr = localStorage.getItem('current_user');
  return userStr ? JSON.parse(userStr) : null;
};

export const setCurrentUser = (user: User): void => {
  localStorage.setItem('current_user', JSON.stringify(user));
};

export const removeCurrentUser = (): void => {
  localStorage.removeItem('current_user');
};

// Auth API calls
export const login = async (credentials: LoginRequest): Promise<AuthResult> => {
  try {
    const response = await axios.post('/auth/login', credentials);
    const result: AuthResult = response.data;

    if (result.isSuccess && result.data) {
      const loginData: LoginResponse = result.data;
      setAuthToken(loginData.token, loginData.expires);
      setCurrentUser(loginData.user);
    }

    return result;
  } catch (error: any) {
    return {
      isSuccess: false,
      message: error.response?.data?.message || 'שגיאה בהתחברות'
    };
  }
};

export const logout = async (): Promise<void> => {
  try {
    await axios.post('/auth/logout');
  } catch (error) {
    // Continue with logout even if API call fails
  } finally {
    removeAuthToken();
    removeCurrentUser();
  }
};

export const forgotPassword = async (request: ForgotPasswordRequest): Promise<AuthResult> => {
  try {
    const response = await axios.post('/auth/forgot-password', request);
    return response.data;
  } catch (error: any) {
    return {
      isSuccess: false,
      message: error.response?.data?.message || 'שגיאה בשליחת בקשת איפוס סיסמה'
    };
  }
};

export const resetPassword = async (request: ResetPasswordRequest): Promise<AuthResult> => {
  try {
    const response = await axios.post('/auth/reset-password', request);
    return response.data;
  } catch (error: any) {
    return {
      isSuccess: false,
      message: error.response?.data?.message || 'שגיאה באיפוס סיסמה'
    };
  }
};

export const validateToken = async (): Promise<boolean> => {
  try {
    const token = getAuthToken();
    if (!token) return false;
    
    await axios.get('/auth/validate');
    return true;
  } catch (error) {
    removeAuthToken();
    removeCurrentUser();
    return false;
  }
};

export const isAuthenticated = (): boolean => {
  return getAuthToken() !== null && getCurrentUser() !== null;
};

// Check if token will expire soon (within 5 minutes)
export const isTokenExpiringSoon = (): boolean => {
  const token = getAuthToken();
  if (!token) return false;
  
  try {
    // Decode JWT to check expiration
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expirationTime = payload.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    const timeUntilExpiration = expirationTime - currentTime;
    
    // Return true if less than 5 minutes until expiration
    return timeUntilExpiration < 5 * 60 * 1000;
  } catch (error) {
    return false;
  }
};